import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  timelineItemClasses,
} from '@mui/lab';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as UpIcon,
  Cancel as DownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as TimelineIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Exporter } from '../../types/exporter';
import { Alert } from '../../types/alerts';
import { getTimeAgo } from 'components/shared';

export interface ActivityTimelineProps {
  exporters: Exporter[];
  alerts: Alert[];
}

interface TimelineEvent {
  id: string;
  type: 'exporter_down' | 'exporter_up' | 'alert_new' | 'alert_acknowledged';
  message: string;
  timestamp: Date;
  severity: 'critical' | 'warning' | 'info' | 'success';
  details?: string;
}

/**
 * Generate timeline events from current data
 * In the future, this could be replaced with a real event stream
 */
function generateTimelineEvents(
  exporters: Exporter[],
  alerts: Alert[]
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Add exporter status events (down exporters)
  exporters
    .filter((e) => e.status === 'down')
    .forEach((exporter) => {
      events.push({
        id: `exporter-down-${exporter.id}`,
        type: 'exporter_down',
        message: `${exporter.display_name || exporter.name} is offline`,
        timestamp: exporter.last_scrape
          ? new Date(exporter.last_scrape)
          : new Date(),
        severity: 'critical',
        details: exporter.target_host,
      });
    });

  // Add alert events
  alerts
    .filter((a) => a.status === 'new' || a.status === 'acknowledged')
    .forEach((alert) => {
      const isNew = alert.status === 'new';
      events.push({
        id: `alert-${alert.id}`,
        type: isNew ? 'alert_new' : 'alert_acknowledged',
        message: isNew
          ? `Alert: ${alert.alertname}`
          : `Alert acknowledged: ${alert.alertname}`,
        timestamp: new Date(alert.updated_at || alert.starts_at),
        severity: alert.severity === 'critical' ? 'critical' : 'warning',
        details: alert.summary,
      });
    });

  // Sort by timestamp (most recent first)
  events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  return events;
}

function getEventIcon(event: TimelineEvent): React.ReactNode {
  switch (event.type) {
    case 'exporter_down':
      return <DownIcon fontSize="small" />;
    case 'exporter_up':
      return <UpIcon fontSize="small" />;
    case 'alert_new':
      return event.severity === 'critical' ? (
        <ErrorIcon fontSize="small" />
      ) : (
        <WarningIcon fontSize="small" />
      );
    case 'alert_acknowledged':
      return <InfoIcon fontSize="small" />;
    default:
      return <InfoIcon fontSize="small" />;
  }
}

function getEventColor(
  event: TimelineEvent,
  theme: ReturnType<typeof useTheme>
): string {
  switch (event.severity) {
    case 'critical':
      return theme.palette.error.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'success':
      return theme.palette.success.main;
    case 'info':
    default:
      return theme.palette.info.main;
  }
}

interface TimelineContentViewProps {
  events: TimelineEvent[];
  maxEvents?: number;
}

function TimelineContentView({ events, maxEvents = 10 }: TimelineContentViewProps) {
  const theme = useTheme();
  const displayedEvents = events.slice(0, maxEvents);

  if (displayedEvents.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <TimelineIcon
          sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 1 }}
        />
        <Typography color="text.secondary">
          No recent activity
        </Typography>
        <Typography variant="caption" color="text.secondary">
          All systems operational
        </Typography>
      </Box>
    );
  }

  return (
    <Timeline
      sx={{
        p: 0,
        m: 0,
        [`& .${timelineItemClasses.root}:before`]: {
          flex: 0,
          padding: 0,
        },
      }}
    >
      {displayedEvents.map((event, index) => {
        const color = getEventColor(event, theme);
        const isLast = index === displayedEvents.length - 1;

        return (
          <TimelineItem key={event.id}>
            <TimelineSeparator>
              <TimelineDot
                sx={{
                  backgroundColor: color,
                  boxShadow: `0 0 0 4px ${alpha(color, 0.2)}`,
                  m: 0,
                  p: 0.5,
                }}
              >
                {getEventIcon(event)}
              </TimelineDot>
              {!isLast && (
                <TimelineConnector
                  sx={{ backgroundColor: theme.palette.divider }}
                />
              )}
            </TimelineSeparator>
            <TimelineContent sx={{ py: 1, px: 2 }}>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {event.message}
              </Typography>
              {event.details && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {event.details}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {getTimeAgo(event.timestamp.toISOString())}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
}

/**
 * Activity Timeline
 *
 * Sidebar or collapsible panel showing recent events:
 * - Exporter status changes (went down/up)
 * - New alerts triggered
 * - Alerts acknowledged/dismissed
 *
 * On desktop: Right sidebar
 * On mobile: Collapsible panel or bottom sheet
 *
 * @example
 * <ActivityTimeline exporters={exporters} alerts={alerts} />
 */
export function ActivityTimeline({ exporters, alerts }: ActivityTimelineProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(!isMobile);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const events = useMemo(
    () => generateTimelineEvents(exporters, alerts),
    [exporters, alerts]
  );

  const hasEvents = events.length > 0;

  // Mobile: Bottom sheet drawer
  if (isMobile) {
    return (
      <Box>
        {/* Toggle Button */}
        <Paper
          onClick={() => setDrawerOpen(true)}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            borderRadius: 2,
            border: hasEvents
              ? `1px solid ${alpha(theme.palette.warning.main, 0.5)}`
              : `1px solid ${theme.palette.divider}`,
            backgroundColor: hasEvents
              ? alpha(theme.palette.warning.main, 0.05)
              : 'background.paper',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TimelineIcon
              sx={{
                color: hasEvents
                  ? theme.palette.warning.main
                  : theme.palette.grey[500],
              }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight="medium">
                Recent Activity
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {hasEvents ? `${events.length} recent events` : 'No recent activity'}
              </Typography>
            </Box>
          </Box>
          <ExpandMoreIcon />
        </Paper>

        {/* Bottom Sheet Drawer */}
        <SwipeableDrawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          disableSwipeToOpen
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '70vh',
            },
          }}
        >
          <Box>
            {/* Handle */}
            <Box
              sx={{
                width: 40,
                height: 4,
                backgroundColor: theme.palette.grey[300],
                borderRadius: 2,
                mx: 'auto',
                mt: 1,
              }}
            />
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Recent Activity
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            {/* Content */}
            <Box sx={{ p: 2, maxHeight: '60vh', overflow: 'auto' }}>
              <TimelineContentView events={events} maxEvents={20} />
            </Box>
          </Box>
        </SwipeableDrawer>
      </Box>
    );
  }

  // Desktop: Collapsible sidebar panel
  return (
    <Box sx={{ mb: 3 }}>
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            cursor: 'pointer',
            backgroundColor: hasEvents
              ? alpha(theme.palette.warning.main, 0.05)
              : 'transparent',
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          <TimelineIcon
            sx={{
              mr: 1.5,
              color: hasEvents
                ? theme.palette.warning.main
                : theme.palette.grey[500],
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Recent Activity
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasEvents ? `${events.length} recent events` : 'No recent activity'}
            </Typography>
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Content */}
        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ p: 1 }}>
            <TimelineContentView events={events} maxEvents={10} />
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
}

export default ActivityTimeline;
